import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n, LANGS } from "@/lib/i18n";
import {
  ArrowRight, LogIn,
  Megaphone, Crown, BookHeart, HeartHandshake,
  CalendarDays, BookText, Music2,
  Home, BookOpen, HandCoins, LayoutGrid,
  Radio, HandHeart, Car, Users, GraduationCap, Info, MapPin, Images, Code2,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { SignInSheet, PublicChurchProvider, usePublicChurch } from "@/pages/public/PublicShell";
import ChurchHero from "@/components/ChurchHero";
import MusicButton from "@/components/MusicButton";
import GalleryStrip from "@/components/GalleryStrip";
import LiveBadge from "@/components/LiveBadge";
import { useLiveStatus } from "@/lib/useLiveStatus";
import { isEnabled, applyTileOrder } from "@/lib/features";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`; // unused; retained for parity

/**
 * Bethanie Haitian SDA Church — public welcome page.
 *
 * Visitors can browse Announcements, Events, Sermons, Songbook, Church
 * Board, Pastor and Departments via the tiles below, plus Bible, Giving
 * and Live via the bottom nav / quick-fact chips — no login required.
 * Admin-only sections (Members, Branding, Subscription) remain gated.
 */

export default function Landing() {
  return (
    <PublicChurchProvider>
      <LandingInner />
    </PublicChurchProvider>
  );
}

function LandingInner() {
  const { user } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const publicChurch = usePublicChurch();
  const liveStatus = useLiveStatus(publicChurch?.church_code);
  const [signInOpen, setSignInOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    if (user) {
      if (user.role === "super_admin") navigate("/super-admin", { replace: true });
      else navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const openSignIn = () => setSignInOpen(true);

  const PUBLIC_TILES = [
    // Announcements intentionally hidden on the public landing per admin
    // request — visitors don't need to see internal member updates.
    // Still accessible to logged-in members via Dashboard.
    { key: "events", to: "/public/events", icon: CalendarDays, label: t("events"), desc: t("descEvents") },
    { key: "sermons", to: "/public/sermons", icon: BookText, label: t("sermons"), desc: t("descSermons") },
    { key: "pastor", to: "/public/pastor", icon: BookHeart, label: t("pastor"), desc: t("descPastor") },
    { key: "firstElder", to: "/public/first-elder", icon: BookHeart, label: t("firstElder"), desc: t("descFirstElder") },
    { key: "prayer", to: "/public/prayer", icon: HandHeart, label: t("prayerWall"), desc: t("communityPrayerRequests") },
    { key: "live", to: "/public/live", icon: Radio, label: t("live"), desc: t("descLive") },
    { key: "carpool", to: "/public/carpool", icon: Car, label: t("carpool") || "Carpool", desc: t("shareRidesToService") },
    { key: "giving", to: "/public/giving", icon: HandCoins, label: t("giving"), desc: t("descGiving") },
    {
      key: "sabbathLessons",
      to: "/public/sabbath-lessons",
      icon: GraduationCap,
      label: t("sabbathLessons"),
      desc: t("sabbathLessonsDesc"),
    },
  ];
  const visibleTiles = applyTileOrder(
    PUBLIC_TILES.filter((tile) => isEnabled(publicChurch?.feature_flags, tile.key, "public", publicChurch?._platform_disabled)),
    publicChurch?.tile_orders?.public,
  );

  const bottomItems = [
    {
      icon: Home, label: t("home"), id: "home", active: true,
      onClick: () => window.scrollTo({ top: 0, behavior: "smooth" }),
    },
    { icon: BookOpen, label: t("bible"), id: "bible", onClick: () => navigate("/public/bible") },
    { icon: Music2, label: t("songbook"), id: "songbook", onClick: () => navigate("/public/songbook") },
    { icon: LayoutGrid, label: t("more"), id: "more", onClick: () => setMoreOpen(true) },
  ];

  const moreLinks = [
    { key: "about", to: "/public/about", label: t("aboutUs"), desc: t("aboutUsDesc"), icon: Info },
    { key: "visit", to: "/public/visit", label: t("visitInPerson"), desc: t("visitInPersonDesc"), icon: MapPin },
  ].filter((l) => isEnabled(publicChurch?.feature_flags, l.key, "public", publicChurch?._platform_disabled));
  // About the developer is app-wide (not tenant-toggled) so it always
  // shows in the public More dialog, regardless of feature flags.
  moreLinks.push({
    key: "about-developer",
    to: "/public/about-developer",
    label: t("aboutTheDeveloper") || "About the developer",
    desc: t("aboutTheDeveloperDesc") || "Meet the person behind the app.",
    icon: Code2,
  });

  return (
    <div className="mobile-shell" data-testid="landing-page">
        <header className="sticky-top px-5 pt-4 pb-3 flex items-center justify-between" data-testid="landing-top">
          <div className="flex items-center gap-2 min-w-0">
            {publicChurch?.logo_url ? (
              <img
                src={publicChurch.logo_url}
                alt={publicChurch?.name || "Church logo"}
                data-testid="landing-church-logo"
                className="w-11 h-11 rounded-full object-contain bg-white border border-stone-200 p-0.5 shrink-0"
              />
            ) : (
              <div className="w-11 h-11 rounded-full grid place-items-center text-white font-semibold tenant-bg shrink-0">
                {publicChurch?.name?.[0] || "B"}
              </div>
            )}
            <div className="min-w-0">
              <div className="label-eyebrow">{t("welcomeTo")}</div>
              <div className="font-serif-display text-sm truncate">
                {publicChurch?.name || t("appNameShort")}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="landing-language-switcher"
                className="rounded-full px-3 h-11 min-w-[44px] grid place-items-center border border-stone-200 bg-white text-xs font-semibold tracking-wider"
              >
                {LANGS.find((l) => l.id === lang)?.short || "EN"}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuLabel>{t("language")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {LANGS.map((l) => (
                  <DropdownMenuItem
                    key={l.id}
                    data-testid={`landing-lang-${l.id}`}
                    onClick={() => setLang(l.id)}
                    className={lang === l.id ? "font-semibold" : ""}
                  >
                    {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <ChurchHero
          church={publicChurch}
          eyebrow={`${t("welcome")}, ${t("guest") || "guest"}`}
          testId="landing-hero"
          isLive={liveStatus.is_live}
          onLiveTap={() => navigate("/public/live")}
        />

        {/* Primary CTA — Member sign-in */}
        <div className="px-5 mt-6 space-y-5 pb-4">
          <button
            onClick={openSignIn}
            data-testid="landing-member-btn"
            className="w-full rounded-2xl p-5 text-white flex items-center gap-3 shadow-lg hover:opacity-95 transition"
            style={{ backgroundColor: "var(--tenant-primary, #7A1F2B)" }}
          >
            <div className="w-10 h-10 rounded-xl bg-white/15 grid place-items-center">
              <LogIn className="w-5 h-5" />
            </div>
            <div className="flex-1 text-left">
              <div className="font-serif-display font-semibold text-base">{t("memberSignIn")}</div>
              <div className="text-xs opacity-85 mt-0.5">
                {t("memberSignInSub")}
              </div>
            </div>
            <ArrowRight className="w-4 h-4 opacity-80" />
          </button>

          {/* Public preview tiles — 3-column square grid */}
          <section className="pt-1" data-testid="landing-quick-actions">
            <div className="px-1 mb-3 flex items-end justify-between gap-3">
              <div className="min-w-0">
                <div className="label-eyebrow">{t("exploreEyebrow")}</div>
                <h2 className="font-serif-display text-lg mt-0.5">{t("browseOurChurch")}</h2>
              </div>
            </div>

            {isEnabled(publicChurch?.feature_flags, "gallery", "public", publicChurch?._platform_disabled)
              && publicChurch?.church_code
              && !publicChurch?.gallery_in_hero && (
              <div className="mb-3">
                <GalleryStrip
                  churchCode={publicChurch.church_code}
                  target="/public/gallery"
                  label={publicChurch?.gallery_title || t("gallery")}
                  testId="landing-gallery-strip"
                />
              </div>
            )}

            <div className="grid grid-cols-3 gap-2">
              {visibleTiles.map((tile) => {
                const Icon = tile.icon;
                const testId = `landing-tile-${tile.label.toLowerCase().replace(/[^a-z]+/g, "-")}`;
                const showLiveBadge = tile.key === "live" && liveStatus.is_live;
                const tileInner = (
                  <>
                    {showLiveBadge && <LiveBadge data-testid="landing-live-badge" />}
                    <div
                      className="w-11 h-11 rounded-xl grid place-items-center shrink-0"
                      style={{ backgroundColor: "color-mix(in srgb, var(--tenant-primary) 12%, transparent)" }}
                    >
                      <Icon className="w-5 h-5 tenant-text" strokeWidth={1.7} />
                    </div>
                    <div className="font-serif-display font-semibold text-[13px] leading-tight">{tile.label}</div>
                  </>
                );
                const tileClass = "relative aspect-square bg-white border border-stone-200 rounded-2xl p-3 hover:border-stone-300 transition flex flex-col items-center justify-center text-center gap-2";
                if (tile.external) {
                  return (
                    <a
                      key={tile.to}
                      href={tile.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={testId}
                      className={tileClass}
                    >
                      {tileInner}
                    </a>
                  );
                }
                return (
                  <Link
                    key={tile.to}
                    to={tile.to}
                    data-testid={testId}
                    className={tileClass}
                  >
                    {tileInner}
                  </Link>
                );
              })}
            </div>
          </section>
        </div>

        {/* Bottom nav — Bible/Giving now navigate publicly */}
        <nav className="bottom-nav" data-testid="landing-bottom-nav">
          <div className="mx-auto max-w-[480px] px-3 py-2 flex justify-around">
            {bottomItems.map((it) => {
              const Icon = it.icon;
              const node = (
                <button
                  key={it.id}
                  onClick={it.onClick}
                  data-testid={`landing-bottomnav-${it.id}`}
                  className="flex flex-col items-center gap-1 py-1 px-3 min-w-[56px]"
                >
                  <Icon
                    className="w-5 h-5"
                    style={{ color: it.active ? "var(--tenant-primary)" : "#78716C" }}
                    strokeWidth={it.active ? 2.2 : 1.6}
                  />
                  <span
                    className="text-[10px] font-medium tracking-wide"
                    style={{ color: it.active ? "var(--tenant-primary)" : "#78716C" }}
                  >
                    {it.label}
                  </span>
                </button>
              );
              // Slot the Music tab between "Songbook" and "More".
              if (it.id === "more") {
                return (
                  <React.Fragment key={it.id}>
                    <MusicButton />
                    {node}
                  </React.Fragment>
                );
              }
              return node;
            })}
          </div>
        </nav>

        <SignInSheet open={signInOpen} onOpenChange={setSignInOpen} />

        {/* "More" dialog — opened by bottom-nav More button */}
        <Dialog open={moreOpen} onOpenChange={setMoreOpen}>
          <DialogContent className="max-w-xs w-[calc(100vw-2rem)] p-4" data-testid="landing-more-dialog">
            <DialogHeader className="min-w-0">
              <DialogTitle className="font-serif-display">{t("moreDialogTitle")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 mt-2 min-w-0 w-full">
              {moreLinks.map((it) => {
                const Icon = it.icon;
                return (
                  <Link
                    key={it.to}
                    to={it.to}
                    onClick={() => setMoreOpen(false)}
                    data-testid={`landing-more-${it.label.toLowerCase()}`}
                    className="w-full flex items-center gap-3 bg-white border border-stone-200 hover:border-stone-300 rounded-2xl p-3 transition min-w-0"
                  >
                    <div className="w-9 h-9 rounded-lg grid place-items-center tenant-soft-bg shrink-0">
                      <Icon className="w-4 h-4 tenant-text" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif-display font-semibold text-sm line-clamp-1">{it.label}</div>
                      <div className="text-[11px] text-stone-500 line-clamp-1">{it.desc}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-stone-300 shrink-0" />
                  </Link>
                );
              })}
              <button
                onClick={() => { setMoreOpen(false); openSignIn(); }}
                data-testid="landing-more-signin"
                className="w-full flex items-center gap-3 rounded-2xl p-3 transition text-white min-w-0"
                style={{ backgroundColor: "var(--tenant-primary)" }}
              >
                <div className="w-9 h-9 rounded-lg grid place-items-center bg-white/15 shrink-0">
                  <LogIn className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="font-serif-display font-semibold text-sm line-clamp-1">{t("signIn")}</div>
                  <div className="text-[11px] opacity-85 line-clamp-1">{t("memberSignInSub")}</div>
                </div>
                <ArrowRight className="w-4 h-4 opacity-70 shrink-0" />
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
  );
}
